// src/hooks/useDeviceManager.ts
import { useState, useEffect, useCallback } from 'react'

interface DeviceGroup {
  audioinput: MediaDeviceInfo[]
  videoinput: MediaDeviceInfo[]
  audiooutput: MediaDeviceInfo[]
}

interface UseDeviceManagerOptions {
  streamRef: React.MutableRefObject<MediaStream | null>
  audioElRef: React.MutableRefObject<HTMLAudioElement | null>
  // Swaps the LiveKit-published track (RTP sender) so the server-side egress
  // recording follows a device switch. Optional: before the interview starts
  // nothing is published and the local stream swap alone is enough.
  replaceLocalTrack?: (track: MediaStreamTrack) => Promise<void>
}

export function useDeviceManager({ streamRef, audioElRef, replaceLocalTrack }: UseDeviceManagerOptions) {
  const [devices, setDevices] = useState<DeviceGroup>({
    audioinput: [],
    videoinput: [],
    audiooutput: [],
  })
  const [selectedAudioInputId, setSelectedAudioInputId] = useState<string>('')
  const [selectedVideoInputId, setSelectedVideoInputId] = useState<string>('')
  const [selectedAudioOutputId, setSelectedAudioOutputId] = useState<string>('')
  const [sinkIdSupported] = useState(() =>
    typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype
  )

  const enumerateAll = useCallback(async () => {
    if (!navigator.mediaDevices) return
    const all = await navigator.mediaDevices.enumerateDevices()
    const grouped: DeviceGroup = { audioinput: [], videoinput: [], audiooutput: [] }
    for (const d of all) {
      if (d.kind in grouped) grouped[d.kind as keyof DeviceGroup].push(d)
    }
    setDevices(grouped)

    // Auto-select first device if nothing selected yet
    if (!selectedAudioInputId && grouped.audioinput.length) {
      setSelectedAudioInputId(grouped.audioinput[0].deviceId)
    }
    if (!selectedVideoInputId && grouped.videoinput.length) {
      setSelectedVideoInputId(grouped.videoinput[0].deviceId)
    }
    if (!selectedAudioOutputId && grouped.audiooutput.length) {
      setSelectedAudioOutputId(grouped.audiooutput[0].deviceId)
    }
  }, [selectedAudioInputId, selectedVideoInputId, selectedAudioOutputId])

  useEffect(() => {
    enumerateAll()
    if (!navigator.mediaDevices) return
    navigator.mediaDevices.addEventListener('devicechange', enumerateAll)
    return () => navigator.mediaDevices.removeEventListener('devicechange', enumerateAll)
  }, [enumerateAll])

  // Shared switch flow: acquire the new track first (so a failing device
  // leaves the current one untouched), swap the LiveKit RTP sender, then
  // swap the local stream and stop the old track. getUserMedia errors
  // propagate to the caller for user-visible feedback (device unplugged,
  // in use by another app, …).
  const swapTrack = useCallback(async (
    stream: MediaStream,
    newTrack: MediaStreamTrack,
    oldTrack: MediaStreamTrack | undefined,
  ) => {
    if (replaceLocalTrack) {
      try {
        await replaceLocalTrack(newTrack)
      } catch (err) {
        // Keep the local swap going — preview/STT still benefit; egress
        // recovers on the next publish.
        console.warn('[devices] LiveKit replaceTrack failed:', err)
      }
    }
    if (oldTrack) {
      stream.removeTrack(oldTrack)
      oldTrack.stop()
    }
    stream.addTrack(newTrack)
  }, [replaceLocalTrack])

  const switchAudioInput = useCallback(async (deviceId: string) => {
    const stream = streamRef.current
    if (!stream) return
    const newStream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: { exact: deviceId }, sampleRate: 48000, channelCount: 1, echoCancellation: true },
    })
    await swapTrack(stream, newStream.getAudioTracks()[0], stream.getAudioTracks()[0])
    setSelectedAudioInputId(deviceId)
  }, [streamRef, swapTrack])

  const switchVideoInput = useCallback(async (deviceId: string) => {
    const stream = streamRef.current
    if (!stream) return
    const newStream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
    })
    await swapTrack(stream, newStream.getVideoTracks()[0], stream.getVideoTracks()[0])
    setSelectedVideoInputId(deviceId)
  }, [streamRef, swapTrack])

  const switchAudioOutput = useCallback(async (deviceId: string) => {
    const el = audioElRef.current
    if (!el || !sinkIdSupported) return
    await (el as any).setSinkId(deviceId)
    setSelectedAudioOutputId(deviceId)
  }, [audioElRef, sinkIdSupported])

  const playTestTone = useCallback(async () => {
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.frequency.value = 440
    gain.gain.value = 0.3
    osc.connect(gain)
    gain.connect(ctx.destination)
    // If setSinkId is supported on AudioContext, route to selected device
    if (sinkIdSupported && selectedAudioOutputId && 'setSinkId' in ctx) {
      try { await (ctx as any).setSinkId(selectedAudioOutputId) } catch { /* fallback to default */ }
    }
    osc.start()
    osc.stop(ctx.currentTime + 0.5)
    setTimeout(() => ctx.close(), 600)
  }, [sinkIdSupported, selectedAudioOutputId])

  return {
    devices,
    selectedAudioInputId,
    selectedVideoInputId,
    selectedAudioOutputId,
    sinkIdSupported,
    switchAudioInput,
    switchVideoInput,
    switchAudioOutput,
    playTestTone,
    enumerateAll,
  }
}